import { Media } from "@knowt/syncing/graphql/schema";
import { getYoutubeId } from "@knowt/syncing/hooks/media/youtubeUtils";
import Image from "next/image";
import { useCallback, useEffect, useRef } from "react";
import YouTube from "react-youtube";

import { themeColors } from "@/utils/themeColors";

import CircularRectTextButton from "@/components/CircularButton/styled/CircularRectTextButton";
import { FlexColumnAlignJustifyCenter } from "@/components/Flex";
import Link from "@/components/wrappers/Link";
import styles from "./embeddedMediaPlayer.module.css";

import { ASSETS_URL } from "@/config/deployConstants";
import { useMediaPlayerContextSelector } from "../hooks/useMediaPlayerContext";

const kaiWatchingTV = `${ASSETS_URL}/images/kai-watching-tv.svg`;

const NonPlayableEmbeddedFrame = ({ media }: { media: Media }) => {
    return (
        <FlexColumnAlignJustifyCenter
            style={{
                width: "100%",
                padding: "5rem",
                aspectRatio: "16/9",
                backgroundColor: themeColors.neutralWhite,
                borderRadius: "2rem",
                textAlign: "center",
                gap: "1.5rem",
            }}>
            <Image
                src={kaiWatchingTV}
                alt="Kai watching TV"
                width={275}
                height={158}
                style={{
                    objectFit: "contain",
                }}
            />
            <FlexColumnAlignJustifyCenter
                style={{
                    gap: "1rem",
                    maxWidth: "49rem",
                }}>
                <p className="bodyBold1">Sorry you’ll need to watch this video outside of Knowt</p>
                <p className="body2">
                    Kai will be able to make flashcards and notes instantly but we’re not able to play this video here.
                </p>
            </FlexColumnAlignJustifyCenter>
            <Link href={media.embedUrl ?? ""} target="_blank" className="strippedLink">
                <CircularRectTextButton
                    component={"div"}
                    className="secondaryText1"
                    sx={{
                        width: "23rem",
                        height: "4.5rem",
                        backgroundColor: themeColors.neutralBlack,
                        color: themeColors.neutralWhite,
                        padding: "1.6rem 3.2rem",
                    }}>
                    take me to watch it
                </CircularRectTextButton>
            </Link>
        </FlexColumnAlignJustifyCenter>
    );
};

const YoutubeFrame = ({ media }: { media: Media }) => {
    const youtubeId = getYoutubeId(media.embedUrl);

    const setCurrentTime = useMediaPlayerContextSelector(state => state.setCurrentTime);
    const setSeekEmbeddedMedia = useMediaPlayerContextSelector(state => state.setSeekEmbeddedMedia);

    const timeGetterRef = useRef<number>(undefined);

    const updateElapsedTime = e => (e.target.getPlayerState() === 1 ? setCurrentTime(e.target.getCurrentTime()) : null);

    const youtubeRef = useCallback(
        node => {
            if (node !== null) {
                const seeker = (time: number) => node.internalPlayer.seekTo(time);
                setSeekEmbeddedMedia(() => seeker);
            }
        },
        [setSeekEmbeddedMedia]
    );

    useEffect(() => {
        return () => {
            clearInterval(timeGetterRef.current);
        };
    }, []);

    return (
        <div
            style={{
                width: "100%",
                borderRadius: "2rem",
            }}>
            <YouTube
                ref={youtubeRef}
                iframeClassName={styles.youtubeFrame}
                videoId={youtubeId}
                style={{
                    width: "100%",
                    margin: "0 auto",
                }}
                onReady={e => {
                    timeGetterRef.current = setInterval(() => updateElapsedTime(e), 100);
                }}
            />
        </div>
    );
};

export const EmbeddedMediaPlayer = ({ media }: { media: Media }) => {
    if (!media.embedUrl) return null;
    const youtubeId = getYoutubeId(media.embedUrl);

    if (youtubeId) {
        return <YoutubeFrame media={media} />;
    }

    return <NonPlayableEmbeddedFrame media={media} />;
};
